<script setup lang="ts">
import { computed } from 'vue';

import { play, previewChannel } from '@/audio';
import { VoiceLine, speak, voicePackSize } from '@/audio/voice';
import { UI_ICONS } from '@/assets/sprites/manifest';
import { useSettingsStore } from '@/stores/settings';

const settings = useSettingsStore();

const percent = (value: number) => `${Math.round(value * 100)}%`;

const masterLabel = computed(() => (settings.muted ? 'muted' : percent(settings.master)));

function close(): void {
  play('close');
  settings.open = false;
}
</script>

<template>
  <Teleport to="body">
    <div v-if="settings.open" class="settings" role="dialog" aria-modal="true" @keyup.esc="close">
      <div class="settings__scrim" @click="close"></div>

      <div class="settings__panel panel">
        <div class="panel__header">
          <img :src="UI_ICONS.settings" alt="" width="22" height="22" />
          <h2>Settings</h2>
          <div class="spacer"></div>
          <button class="btn btn--sm" @click="close">Close</button>
        </div>

        <div class="panel__body stack">
          <section class="group">
            <div class="group__head">
              <h3>Sound</h3>
              <label class="toggle">
                <input v-model="settings.muted" type="checkbox" />
                <span>Mute everything</span>
              </label>
            </div>

            <div class="slider" :class="{ 'is-off': settings.muted }">
              <label for="vol-master">Master <span class="muted">{{ masterLabel }}</span></label>
              <input
                id="vol-master"
                v-model.number="settings.master"
                type="range"
                min="0"
                max="1"
                step="0.05"
                :disabled="settings.muted"
                @change="previewChannel('ui')"
              />
            </div>

            <div class="slider" :class="{ 'is-off': settings.muted }">
              <label for="vol-ui">
                Interface <span class="muted">{{ percent(settings.ui) }}</span>
              </label>
              <input
                id="vol-ui"
                v-model.number="settings.ui"
                type="range"
                min="0"
                max="1"
                step="0.05"
                :disabled="settings.muted"
                @change="previewChannel('ui')"
              />
              <p class="hint">Clicks, panels and menus.</p>
            </div>

            <div class="slider" :class="{ 'is-off': settings.muted }">
              <label for="vol-game">
                World <span class="muted">{{ percent(settings.game) }}</span>
              </label>
              <input
                id="vol-game"
                v-model.number="settings.game"
                type="range"
                min="0"
                max="1"
                step="0.05"
                :disabled="settings.muted"
                @change="previewChannel('game')"
              />
              <p class="hint">Construction, training, marches and battle horns.</p>
            </div>

            <div v-if="voicePackSize() > 0" class="slider" :class="{ 'is-off': settings.muted }">
              <label for="vol-voice">
                Voices <span class="muted">{{ percent(settings.voice) }}</span>
              </label>
              <input
                id="vol-voice"
                v-model.number="settings.voice"
                type="range"
                min="0"
                max="1"
                step="0.05"
                :disabled="settings.muted"
                @change="speak(VoiceLine.SELECT)"
              />
              <p class="hint">Your men answering orders.</p>
            </div>

            <div class="slider" :class="{ 'is-off': settings.muted || !settings.ambienceOn }">
              <label for="vol-ambience">
                Ambience <span class="muted">{{ percent(settings.ambience) }}</span>
              </label>
              <input
                id="vol-ambience"
                v-model.number="settings.ambience"
                type="range"
                min="0"
                max="1"
                step="0.05"
                :disabled="settings.muted || !settings.ambienceOn"
              />
              <label class="toggle">
                <input v-model="settings.ambienceOn" type="checkbox" />
                <span>Play background ambience</span>
              </label>
            </div>

            <div class="slider" :class="{ 'is-off': settings.muted || !settings.musicOn }">
              <label for="vol-music">
                Music <span class="muted">{{ percent(settings.music) }}</span>
              </label>
              <input
                id="vol-music"
                v-model.number="settings.music"
                type="range"
                min="0"
                max="1"
                step="0.05"
                :disabled="settings.muted || !settings.musicOn"
              />
              <label class="toggle">
                <input v-model="settings.musicOn" type="checkbox" />
                <span>Play music</span>
              </label>
            </div>
          </section>

          <section class="group">
            <h3>Play</h3>
            <label class="toggle">
              <input v-model="settings.confirmDestructive" type="checkbox" />
              <span>Confirm before costly actions</span>
            </label>
            <p class="hint">
              Leaving an alliance, cancelling construction, recalling an army. Turning this
              off makes them immediate.
            </p>

            <label class="toggle">
              <input v-model="settings.showBuildingLabels" type="checkbox" />
              <span>Always show building names on the plot</span>
            </label>
            <p class="hint">Off shows them only when you point at a building.</p>
          </section>

          <div class="row">
            <button class="btn btn--sm" @click="settings.reset()">Reset to defaults</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.settings {
  position: fixed;
  inset: 0;
  z-index: 210;
  display: grid;
  place-items: center;
  padding: 1rem;

  &__scrim {
    position: absolute;
    inset: 0;
    background: rgb(8 7 6 / 68%);
  }

  &__panel {
    position: relative;
    width: min(460px, 100%);
    max-height: 88vh;
    overflow-y: auto;
    box-shadow: 0 12px 40px rgb(0 0 0 / 55%);
  }
}

.panel__header img { display: block; }

.group {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding-bottom: 0.7rem;
  border-bottom: 1px solid var(--border);

  &:last-of-type { border-bottom: none; padding-bottom: 0; }

  h3 { margin: 0; font-size: 0.95rem; }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }
}

.slider {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  &.is-off { opacity: 0.45; }

  label {
    display: flex;
    justify-content: space-between;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.85rem;
    color: var(--parchment);
    margin: 0;
  }

  input[type='range'] {
    width: 100%;
    padding: 0;
    accent-color: var(--gold);
    background: none;
    border: none;
  }
}

.toggle {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.85rem;
  color: var(--parchment);
  cursor: inherit;

  input {
    width: auto;
    accent-color: var(--gold);
    cursor: inherit;
  }
}

.hint {
  margin: 0;
  font-size: 0.75rem;
  color: var(--parchment-faint);
  line-height: 1.4;
}
</style>
